/**
 * Guided screen tours — educational walkthroughs shown per screen/tab.
 *
 * Keyed by tourKey. ProjectHub passes `hub-<activeTab>`, ProjectDetails passes
 * `project-<activeTab>`, and standalone pages pass their own key.
 *
 * Each tour: { title, subtitle, icon (lucide name), accent, steps: [{ title, body, bullets? }] }
 * Icons are referenced by name and resolved in ScreenTour via the ICONS map.
 */

export const SCREEN_TOURS = {
  'hub-pipeline': {
    title: 'Pipeline',
    subtitle: 'Where deals live before they become active builds',
    icon: 'Briefcase',
    accent: 'from-blue-600 to-indigo-600',
    steps: [
      {
        title: 'What the Pipeline is',
        body: 'The Pipeline is your deal funnel — every potential project starts here as a lead before any money moves. Track it from first look through bidding and approval.',
      },
      {
        title: 'Gather bids while it\u2019s still a lead',
        body: 'From a pipeline card you can request bids from contractors and suppliers, attach plans and spec sheets, and build your budget — all before committing capital.',
        bullets: ['Request bids by trade', 'Attach plans & spec sheets', 'Compare numbers side-by-side'],
      },
      {
        title: 'Pipeline \u2192 Active project',
        body: 'Once the deal pencils out and financing is lined up, promote it to an Active Project. Your budget, contractors, and documents carry over — no re-entry.',
        bullets: ['Deal pencils out → promote', 'Budget & contractors carry over', 'Kick off the schedule from day one'],
      },
    ],
  },

  'hub-contractors': {
    title: 'Contractors',
    subtitle: 'Build your bench and keep compliance clean',
    icon: 'Users',
    accent: 'from-blue-600 to-cyan-600',
    steps: [
      {
        title: 'Add contractors once, reuse everywhere',
        body: 'Add each contractor to your company roster. They stay available across every project so you never re-type their details.',
      },
      {
        title: 'Request the W-9 up front',
        body: 'When you first add a contractor to DrawReady, request their W-9 right away. It\u2019s a one-time document you\u2019ll need at tax time — get it out of the way at signup.',
        bullets: ['Click the W-9 badge to request or upload', 'Collected once, reused every year', 'Keeps 1099 season painless'],
      },
      {
        title: 'COI is per-project',
        body: 'A Certificate of Insurance is only requested when you add a contractor to a specific project — because coverage requirements change per job.',
        bullets: [
          'Capture project-specific needs (additional insured, coverage limits)',
          'We track the expiration and warn you before it lapses',
          'No blanket COI — always matched to the job',
        ],
      },
    ],
  },

  'hub-bids': {
    title: 'Bid Requests',
    subtitle: 'Get apples-to-apples pricing from your trades',
    icon: 'FileText',
    accent: 'from-blue-600 to-indigo-600',
    steps: [
      {
        title: 'Request bids by trade',
        body: 'Send a scope of work to one or several contractors for a trade and collect their pricing in one place — no scattered emails or texts.',
      },
      {
        title: 'Attach the details',
        body: 'Include plans and spec sheets so everyone bids against the exact same scope. Tighter scope in means tighter, more comparable bids back.',
      },
      {
        title: 'Track responses',
        body: 'See who has responded, follow up on who hasn\u2019t, and move the winning bid straight into your budget.',
      },
    ],
  },

  'hub-bidsheets': {
    title: 'Bid Sheets',
    subtitle: 'Compare every bid line by line',
    icon: 'ChartBar',
    accent: 'from-cyan-600 to-blue-600',
    steps: [
      {
        title: 'One sheet per trade',
        body: 'Each bid sheet lays out the scope and the numbers so you can see exactly what\u2019s included — and what isn\u2019t.',
      },
      {
        title: 'Normalize the numbers',
        body: 'Line up bids side by side to catch missing scope, inflated allowances, or the true low bid once everything is apples-to-apples.',
      },
      {
        title: 'Award and roll into budget',
        body: 'Pick the winner and the amounts flow into your project budget and the contractor\u2019s scope automatically.',
      },
    ],
  },

  'hub-messages': {
    title: 'Messages',
    subtitle: 'Keep every project conversation in one thread',
    icon: 'FileText',
    accent: 'from-sky-600 to-blue-600',
    steps: [
      {
        title: 'One place for all comms',
        body: 'Message contractors and keep the whole conversation attached to the project — no more digging through your phone or inbox.',
      },
      {
        title: 'Everyone stays in the loop',
        body: 'Contractors see and reply from their portal, so questions, approvals, and updates live next to the work they\u2019re about.',
      },
      {
        title: 'A searchable record',
        body: 'Every message is saved with the project, giving you a clean history if a question ever comes up later.',
      },
    ],
  },

  'hub-documents': {
    title: 'Documents',
    subtitle: 'Every file for the project, organized',
    icon: 'FileText',
    accent: 'from-violet-600 to-purple-600',
    steps: [
      {
        title: 'Your project filing cabinet',
        body: 'Store plans, permits, contracts, insurance, spec sheets, and more — all attached to the right project.',
      },
      {
        title: 'Share the right docs',
        body: 'Make documents available to the contractors who need them, so the field is always working from the current version.',
      },
      {
        title: 'Always review-ready',
        body: 'When a bank, buyer, or inspector needs a document, it\u2019s a click away instead of a scramble.',
      },
    ],
  },

  'hub-reviews': {
    title: 'Reviews',
    subtitle: 'Build a track record with your trades',
    icon: 'ChartBar',
    accent: 'from-amber-500 to-orange-600',
    steps: [
      {
        title: 'Rate work as you go',
        body: 'Leave a review for each contractor based on quality, timeliness, and communication — while the job is fresh.',
      },
      {
        title: 'Build your trusted bench',
        body: 'Over time you\u2019ll know exactly which subs to call first, and which to avoid, backed by your own history.',
      },
    ],
  },

  'hub-aiextract': {
    title: 'AI Extract',
    subtitle: 'Turn a bid or invoice into structured data',
    icon: 'Sparkles',
    accent: 'from-fuchsia-600 to-purple-600',
    steps: [
      {
        title: 'Drop in a bid or invoice',
        body: 'Upload a contractor\u2019s bid, invoice, or receipt and let AI pull out the trade, line items, and amounts for you.',
      },
      {
        title: 'Review and use it',
        body: 'Check the extracted details, then push them into your budget or a bid sheet — no manual re-typing.',
      },
    ],
  },

  'hub-compare': {
    title: 'Compare',
    subtitle: 'See your bids and options head to head',
    icon: 'BarChart3',
    accent: 'from-indigo-600 to-blue-600',
    steps: [
      {
        title: 'Compare at a glance',
        body: 'Line up bids or scenarios so the differences in price and scope jump out instantly.',
      },
      {
        title: 'Decide with confidence',
        body: 'Pick the option that actually pencils — not just the lowest sticker price — and move it into your plan.',
      },
    ],
  },

  'reports': {
    title: 'Reports',
    subtitle: 'The numbers behind your portfolio',
    icon: 'ChartBar',
    accent: 'from-slate-700 to-blue-700',
    steps: [
      {
        title: 'See the whole picture',
        body: 'Reports roll up your projects, budgets, and progress so you can spot what\u2019s on track and what needs attention.',
      },
      {
        title: 'Know your performance',
        body: 'Track costs, timelines, and outcomes across projects to sharpen how you budget and price the next deal.',
      },
      {
        title: 'Share when you need to',
        body: 'Pull the figures you need for a partner, buyer, or your own planning — without rebuilding a spreadsheet.',
      },
    ],
  },

  'project-loan': {
    title: 'Loan Details',
    subtitle: 'Track interest and manage draws in one place',
    icon: 'Landmark',
    accent: 'from-emerald-600 to-teal-600',
    steps: [
      {
        title: 'Why this tab exists',
        body: 'Your construction loan funds the build in stages (\u201Cdraws\u201D). This tab is your single source of truth for what\u2019s been drawn, what\u2019s available, and the interest cost of carrying the loan.',
      },
      {
        title: 'How interest is calculated',
        body: 'Interest accrues only on the amount actually drawn, not the full loan. DrawReady computes it in real time: (amount drawn) \u00D7 (rate \u00F7 365) per day.',
        bullets: ['Daily accrual on drawn balance', 'Live running total', 'Record each interest payment as you make it'],
      },
      {
        title: 'Recording a draw',
        body: 'Request a draw, get the bank\u2019s approval, then record the funds. We\u2019re making this seamless — request \u2192 approval \u2192 auto-record in one flow — so you never forget a step.',
        bullets: ['+ New Draw Request', 'Bank approves & deposits', 'Draw recorded automatically'],
      },
    ],
  },

  'project-budget': {
    title: 'Budget & Progress',
    subtitle: 'Track every dollar and keep your progress accurate',
    icon: 'ChartBar',
    accent: 'from-blue-600 to-cyan-600',
    steps: [
      {
        title: 'All-in cost vs. budget',
        body: 'The top of this screen shows your projected all-in cost (construction + property + holding) against your budget, with a live over/under readout and a budget-used bar.',
      },
      {
        title: 'Log costs to the right phase',
        body: 'Every cost belongs to a construction phase. Adding it to the correct phase is what keeps your budget-used and completion % accurate.',
        bullets: [
          'Hard costs — the trade work itself (framing, roofing, etc.)',
          'Holding costs — loan interest, property tax & insurance while you carry the project',
          'Soft costs — permits, design, survey, and professional fees',
        ],
      },
      {
        title: 'Record work performed & materials ordered',
        body: 'When a contractor completes work or you purchase materials, log it against that phase. That is what advances the progress bar and marks the phase % complete.',
        bullets: [
          'Log a payment / cost on the matching phase',
          'Mark materials ordered or work completed',
          'The phase % and overall progress update automatically',
        ],
      },
      {
        title: 'Work with your contractors',
        body: 'Invite each contractor to their own portal. They get a private login to see their scope, upload completion photos, sign lien waivers, and message you — so you are not chasing email threads.',
        bullets: [
          'Send the initial portal invite from the Contractors area',
          'They upload photos & lien waivers before getting paid (your self-inspection)',
          'Keep the conversation going in the portal, not email',
        ],
      },
      {
        title: 'Add your key dates',
        body: 'Optional dates (permit submitted/approved, survey, completion, listing, sold) live in Edit Project. They sharpen your own timeline, valuation, and records — fill in whatever you have.',
      },
    ],
  },

  'project-schedule': {
    title: 'Schedule',
    subtitle: 'Run a build even with zero prior experience',
    icon: 'Calendar',
    accent: 'from-amber-500 to-orange-600',
    steps: [
      {
        title: 'What the Schedule does',
        body: 'The Schedule sequences your trades and keeps everyone coordinated. New to building? It walks you through it so you can run a project confidently.',
      },
      {
        title: 'Kick it off with a nudge',
        body: 'Start by sending the first contractor a schedule nudge asking for their start date and how many days their scope takes. When they answer, the next contractor in line is asked automatically.',
        bullets: ['Nudge sub #1 for start date + duration', 'Auto-advance to the next trade', 'The schedule builds itself as replies come in'],
      },
      {
        title: 'Pre-start check & auto-reflow',
        body: 'As a trade is about to begin (say Framing), we ask \u201Cstill good to start?\u201D If they need to push, we accept the new days, update the schedule, and notify every affected sub down the line.',
        bullets: ['\u201CStill good to start?\u201D confirmation', 'Accepts a push and reflows dates', 'Notifies all downstream subs automatically'],
      },
    ],
  },

  'project-gantt': {
    title: 'Timeline',
    subtitle: 'The visual view of your project over time',
    icon: 'BarChart3',
    accent: 'from-indigo-600 to-blue-600',
    steps: [
      {
        title: 'Timeline vs. Schedule',
        body: 'The Timeline is the visual, calendar-style view of your project. The Schedule is the interactive engine that sequences trades and sends nudges. Same data, two lenses.',
        bullets: ['Timeline = see it at a glance', 'Schedule = drive it & coordinate subs'],
      },
      {
        title: 'Read the bars',
        body: 'Each bar is a phase or trade plotted against the calendar, with a \u201Ctoday\u201D marker so you instantly see what\u2019s ahead of or behind plan.',
      },
      {
        title: 'One source, two views',
        body: 'Changes you make in the Schedule flow straight into the Timeline — flip between them anytime to plan and then present.',
      },
    ],
  },

  'project-spec-sheets': {
    title: 'Spec Sheets',
    subtitle: 'Get the right specs to the right trades — no email chains',
    icon: 'FileText',
    accent: 'from-violet-600 to-purple-600',
    steps: [
      {
        title: 'Upload once, share smartly',
        body: 'Spec sheets define the materials and finishes for the build. Upload them here and tag the trades they apply to.',
      },
      {
        title: 'Auto-published to contractor portals',
        body: 'Tagged spec sheets load automatically into the relevant contractors\u2019 portals, and they\u2019re notified — cutting out dozens of back-and-forth emails.',
        bullets: ['Attach relevant trades to each sheet', 'Auto-loads to those contractors\u2019 portals', 'Contractors get notified'],
      },
      {
        title: 'Add them early',
        body: 'Best practice: attach spec sheets when you\u2019re requesting bids in the Pipeline, so contractors bid against exact specs from day one.',
      },
    ],
  },

  'project-paint': {
    title: 'Paint Colors',
    subtitle: 'Lock finishes so nothing gets guessed on site',
    icon: 'PaintBucket',
    accent: 'from-rose-500 to-pink-600',
    steps: [
      {
        title: 'A single source for finishes',
        body: 'Record every paint color and where it goes — walls, trim, ceilings, exterior — so painters and clients are never guessing.',
      },
      {
        title: 'How to use it',
        body: 'Add each color with its brand, code, sheen, and location. Attach it to the project so it shows up alongside the specs your painter sees.',
        bullets: ['Brand + color code + sheen', 'Assign to a room / surface', 'Shared with the painter, no separate email'],
      },
    ],
  },

  'analysis-suite': {
    title: 'Analysis Suite',
    subtitle: 'Decide in 30 seconds whether a deal is bank-ready',
    icon: 'Sparkles',
    accent: 'from-purple-600 to-fuchsia-600',
    steps: [      {
        title: 'Your underwriting shortcut',
        body: 'The Analysis Suite quickly tells you whether a deal is worth taking to the bank — before you spend time packaging it.',
      },
      {
        title: 'Every bank funds differently',
        body: 'Lenders each have their own rules for how they fund deals and how much cash the borrower must bring. Enter your bank\u2019s targets and we\u2019ll measure the deal against them.',
        bullets: [
          'Target DSCR (e.g. your bank wants 1.1)',
          'Required borrower equity %',
          'Max LTC / LTV and ARV',
        ],
      },
      {
        title: 'Break-even & profitability',
        body: 'We show where you need to be on rents, cost, and profit to clear the bank\u2019s bar — so borrowers know their break-even and lenders trust the deal.',
      },
      {
        title: 'Pass / needs-work readout',
        body: 'Get a clear \u201Cbank-ready\u201D or \u201Cneeds work\u201D result with the exact gaps, so you fix the deal or move on fast.',
      },
    ],
  },

  'lender-portfolio': {
    title: 'Portfolio Overview',
    subtitle: 'Every borrower and loan at a glance',
    icon: 'Landmark',
    accent: 'from-slate-800 to-slate-900',
    steps: [
      {
        title: 'Your whole book in one screen',
        body: 'Each borrower expands to show their loans with drawn vs. available, interest economics, and compliance — answer any question in about 30 seconds.',
      },
      {
        title: 'Spot risk instantly',
        body: 'The badges and filters surface what needs attention without digging.',
        bullets: [
          'Compliance gaps — missing lien waivers or completion photos',
          'Over budget — a line item has run past its budget',
          'Expiring \u2264 60 days — loans approaching maturity',
        ],
      },
      {
        title: 'Self-inspection, built in',
        body: 'Contractors only get paid after uploading completion photos, signing lien waivers, and (for MEP/concrete) an inspection — so you get verified progress without sending an inspector. Borrowers release payment to the contractor only once those docs are in.',
      },
    ],
  },

  'lender-draws': {
    title: 'Draws Requested',
    subtitle: 'Approve a draw in one click — no spreadsheets',
    icon: 'Inbox',
    accent: 'from-blue-700 to-indigo-700',
    steps: [
      {
        title: 'Requests land here for decision',
        body: 'When a borrower submits a draw request, it appears here with the amount, note, and project — replacing the email-and-spreadsheet back-and-forth.',
      },
      {
        title: 'Approve = auto-recorded',
        body: 'One click to Approve records the draw against the loan, marks the funds approved, and updates the borrower\u2019s available balance automatically. Reject sends it back with your note.',
        bullets: ['Approve & record in one step', 'Balance & available funds update instantly', 'Reject with a reason'],
      },
      {
        title: 'A clean audit trail',
        body: 'Every decision is timestamped with who approved it and why — so your file is always review-ready.',
      },
    ],
  },

  'lender-upcoming': {
    title: 'Upcoming Draws',
    subtitle: 'See what\u2019s coming before the request arrives',
    icon: 'CalendarClock',
    accent: 'from-teal-700 to-emerald-700',
    steps: [
      {
        title: 'Anticipate the next draws',
        body: 'We surface phases that are complete or have materials purchased but haven\u2019t been fully drawn yet — grouped by loan with a suggested amount.',
      },
      {
        title: 'Plan your capital',
        body: 'Use the totals to forecast upcoming funding across the portfolio, then expand any loan to see the specific phases driving it.',
      },
    ],
  },
};

export function getTour(tourKey) {
  return SCREEN_TOURS[tourKey] || null;
}